import Echo from 'laravel-echo';
import * as Sentry from '@sentry/react';

import Pusher from 'pusher-js';
window.Pusher = Pusher;

// BRO-877: Track Echo disconnects for extended-disconnect detection (D11).
// A momentary blip is normal; a sustained disconnect (>30s) means the user
// is silently without realtime updates and warrants a reported event.
const ECHO_EXTENDED_DISCONNECT_MS = 30_000;
let echoDisconnectedAt = null;

// Defensive Sentry breadcrumb wrapper: handlers MUST NOT throw, since a
// thrown handler would itself be reported by Sentry's global handler and
// potentially loop. console.warn on internal failure, never re-throw.
function safeBreadcrumb(category, message, level, data) {
    try {
        Sentry.addBreadcrumb({ category, message, level, data });
    } catch (e) {
        console.warn('Sentry breadcrumb failed:', e);
    }
}

function safeCaptureMessage(message, level, tags) {
    try {
        Sentry.captureMessage(message, { level, tags });
    } catch (e) {
        console.warn('Sentry captureMessage failed:', e);
    }
}

// BRO-1387: the SERVER decides where the websocket is, not the build.
//
// window.reverbConfig is rendered into every page by app.blade.php from
// Laravel config, so it is correct for whichever environment the container is
// running in. It has been there all along and nothing read it: this file used
// import.meta.env instead, which Vite bakes in at BUILD time.
//
// That difference decides whether one image can serve dev, staging and live.
// Baked in, the websocket host is part of the artifact, so promoting a tested
// image from staging to live is impossible and each environment needs its own
// build with its own variables. Read at runtime, the same image runs anywhere
// and what was tested on staging is bit-identical to what goes live.
//
// import.meta.env stays as the fallback for any page that does not extend the
// app layout, and for local dev where the values come from .env.
const reverbConfig = window.reverbConfig ?? {};
const reverbKey = reverbConfig.key || import.meta.env.VITE_REVERB_APP_KEY;

// Only initialize Echo if we have the required Reverb configuration
if (reverbKey) {
    const scheme = reverbConfig.scheme || import.meta.env.VITE_REVERB_SCHEME || 'https';
    const port = parseInt(reverbConfig.port ?? import.meta.env.VITE_REVERB_PORT ?? '443');
    
    // For standard ports (80/443), don't specify port in the connection
    const shouldSpecifyPort = (scheme === 'https' && port !== 443) || (scheme === 'http' && port !== 80);
    
    window.Echo = new Echo({
        broadcaster: 'reverb',
        key: reverbKey,
        wsHost: reverbConfig.host || import.meta.env.VITE_REVERB_HOST,
        wsPort: shouldSpecifyPort ? port : undefined,
        wssPort: shouldSpecifyPort ? port : undefined,
        forceTLS: scheme === 'https',
        enabledTransports: ['ws', 'wss'],
        // BRO-564: Activity timeout settings for connection health
        activityTimeout: 120000,  // 2 minutes - send ping if no activity
        pongTimeout: 30000,       // 30 seconds - wait for pong response
    });

    // BRO-564: Set up connection state change listeners for reconnection handling
    // BRO-877 (D11): Lifecycle events also emit Sentry breadcrumbs so missed
    // realtime broadcasts are diagnosable post-hoc; extended disconnects fire
    // a captureMessage as their own event class.
    if (window.Echo.connector?.pusher?.connection) {
        const connection = window.Echo.connector.pusher.connection;

        connection.bind('state_change', (states) => {
            console.debug('🔌 Echo connection state changed:', states.previous, '->', states.current);
            safeBreadcrumb('echo', `state ${states.previous} -> ${states.current}`, 'info', {
                previous: states.previous,
                current: states.current,
            });
            window.dispatchEvent(new CustomEvent('echo:state-change', {
                detail: { previous: states.previous, current: states.current }
            }));
        });

        connection.bind('connected', () => {
            console.debug('🟢 Echo connected');
            safeBreadcrumb('echo', 'connected', 'info');
            // If we were disconnected for longer than the threshold, this is
            // worth knowing about — broadcasts during the gap may have been missed.
            if (echoDisconnectedAt !== null) {
                const downSeconds = Math.round((Date.now() - echoDisconnectedAt) / 1000);
                if (downSeconds * 1000 > ECHO_EXTENDED_DISCONNECT_MS) {
                    safeCaptureMessage(
                        `Echo reconnected after extended disconnect (${downSeconds}s)`,
                        'warning',
                        { event_class: 'echo_extended_disconnect' },
                    );
                }
                echoDisconnectedAt = null;
            }
            window.dispatchEvent(new CustomEvent('echo:connected'));
        });

        connection.bind('disconnected', () => {
            console.debug('🔴 Echo disconnected');
            safeBreadcrumb('echo', 'disconnected', 'warning');
            echoDisconnectedAt = Date.now();
            window.dispatchEvent(new CustomEvent('echo:disconnected'));
        });

        connection.bind('connecting', () => {
            safeBreadcrumb('echo', 'connecting', 'info');
        });

        connection.bind('unavailable', () => {
            safeBreadcrumb('echo', 'unavailable', 'warning');
        });

        connection.bind('failed', () => {
            // Sustained connection failure — user is silently without realtime
            // updates indefinitely. Worth its own captured event.
            safeBreadcrumb('echo', 'failed', 'error');
            safeCaptureMessage('Echo connection failed', 'error', {
                event_class: 'echo_failed',
            });
        });

        connection.bind('error', (err) => {
            try {
                Sentry.captureException(err, {
                    tags: { error_class: 'echo_error' },
                });
            } catch (e) {
                console.warn('Sentry captureException failed for echo error:', e);
            }
        });
    }
} else {
    // Fallback for missing configuration
    console.debug('Reverb configuration missing - Echo not initialized');
    window.Echo = null;
}
